// Constants
import { projectManagerAgentId } from "@ms/uno-constants/lib/local/AgentConstants";
import { DueDateCategory } from "@ms/uno-constants/lib/local/DateConstants";
import { AllTasksGroup, CustomFieldFlagNoGroup, CustomFieldFlagYesGroup, PersonalSourceGroup, PrivateSourceGroup, SharedSourceGroup, TaskGroupingOption, TasksWithoutCustomFieldValueGroup, TasksWithoutGoalAssociationGroup, UnassignedTaskGroup, UnbucketedTaskGroup, UnlabelledTaskGroup } from "@ms/uno-constants/lib/local/GroupingConstants";
import { CopilotWorkflowUnifiedState } from "@ms/uno-constants/lib/local/WorkflowConstants";
import { ContainerType, CustomFieldValueType } from "../service/planner/enumerations/IPlannerEnumerations";
import { TaskKind } from "../client/planner/task/ITask";
// Utilities
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { getSystemTimeZone, getTodayPlainDate, isInCurrentWeek, isInNextWeek, isPast, isToday, isTomorrow } from "@ms/uno-utilities/lib/local/DateUtilities-temporal";
import { Temporal } from "temporal-polyfill-lite";
import { DateWithOptionalTime } from "../client/planner/common/DateWithOptionalTime";
import { ConvertPriorityNameToPriority } from "./ConversionUtilities";
import { resolveCustomFieldDefinitionIdFromGroupingOption } from "./CustomFieldDefinitionUtilities";
import { compareByDueDate, compareByFinishDate } from "./TaskSortingUtilities";
/**
 * Key values for cluttered groups that are not based on actual task properties,
 * but are used to group tasks that would otherwise fall into their own
 * individual groups (e.g. all completed tasks, or all tasks with past due dates).
 * These are used in the grouping logic but are never persisted to the service.
 */ export var ClutteredGroupKey = /*#__PURE__*/ function(ClutteredGroupKey) {
    ClutteredGroupKey["Completed"] = "__cluttered_completed__";
    ClutteredGroupKey["Older"] = "__cluttered_older__";
    return ClutteredGroupKey;
}({});
/**
 * Get the task group(s) for a given task for a grouping option
 *
 * @param task Task to get groups for
 * @param groupBy Grouping option
 * @param configProvider Config provider used for flight checks
 * @param validGroupIds Set of valid Ids for the grouping option (only required when grouping by bucket or goal)
 * @param timeZone Optional timezone string. Pass this when processing many tasks to avoid repeated timezone resolution.
 * @param todayPlainDate Optional pre-computed today's PlainDate. Pass this when processing many tasks to avoid repeated computation.
 * @param cfdValueTypes Map from custom field definition ID to its value type for the current plan.
 * Used so that tasks missing a value for a Date-type CFD are grouped under "No date".
 * @returns Group ID(s) for the task
 */ export function getTaskGroups(task, groupBy, configProvider, validGroupIds = new Set(), timeZone = getSystemTimeZone(), todayPlainDate = getTodayPlainDate(timeZone), cfdValueTypes = new Map(), planContainerTypeLookup) {
    switch(groupBy){
        case TaskGroupingOption.Bucket:
            return task.bucketId && validGroupIds.has(task.bucketId) ? task.bucketId : UnbucketedTaskGroup;
        case TaskGroupingOption.Goal:
            const validGoalIds = task.goalIds.filter((goalId)=>validGroupIds.has(goalId));
            return validGoalIds.length > 0 ? validGoalIds : TasksWithoutGoalAssociationGroup;
        case TaskGroupingOption.DueDate:
            return dueDateCategoryToString(getDueDateCategory(task.dueDateTime?.getLocalDate(timeZone), todayPlainDate));
        case TaskGroupingOption.FinishDate:
            return dueDateCategoryToString(getDueDateCategory(task.finishDateTime?.getLocalDate(timeZone), todayPlainDate));
        case TaskGroupingOption.Status:
            return task.status.toString();
        case TaskGroupingOption.Assignee:
            const assignees = [];
            // Add project manager agent if assigned
            if (task.isAssignedToCopilotAgent()) {
                assignees.push(projectManagerAgentId);
            }
            // Add user assignees
            if (task.userAssignments.length > 0) {
                assignees.push(...task.userAssignments.map((ua)=>ua.user.id));
            }
            return assignees.length > 0 ? assignees : UnassignedTaskGroup;
        case TaskGroupingOption.Priority:
            return task.priority.toString();
        case TaskGroupingOption.Category:
            return task.planView.appliedCategoryIds.length > 0 ? task.planView.appliedCategoryIds : UnlabelledTaskGroup;
        case TaskGroupingOption.Plan:
            return task.planId;
        case TaskGroupingOption.ProjectManagerWorkflowState:
            return task.getCopilotWorkflowCategory().toString();
        case TaskGroupingOption.Source:
            return getSourceGroup(task, planContainerTypeLookup);
        case TaskGroupingOption.None:
            return AllTasksGroup;
        default:
            if (configProvider().flights.EnableCustomFields) {
                const cfdId = resolveCustomFieldDefinitionIdFromGroupingOption(groupBy);
                if (cfdId) {
                    return getCustomFieldGroupId(task, cfdId, todayPlainDate, cfdValueTypes);
                }
            }
            return AllTasksGroup;
    }
}
/**
 * Returns the group ID for a task based on a custom field definition.
 * @param task The task whose custom field values are inspected
 * @param cfdId The custom field definition ID to group by
 * @param todayPlainDate Today's PlainDate used for date category calculation
 * @param cfdValueTypes Map from custom field definition ID to its value type
 * @returns The choice option ID for SingleChoiceText fields, the date category string for Date fields,
 * the Yes/No group key for Flag fields, or TasksWithoutCustomFieldValueGroup if the field is absent or an unsupported type.
 * For Date-type CFDs with a missing value, returns "No date".
 */ function getCustomFieldGroupId(task, cfdId, todayPlainDate, cfdValueTypes) {
    const cfv = task.customFieldValues.find((cfv)=>cfv.customFieldDefinitionId === cfdId);
    const valueType = cfdValueTypes.get(cfdId);
    if (valueType === CustomFieldValueType.SingleChoiceText) {
        return cfv?.value ?? TasksWithoutCustomFieldValueGroup;
    } else if (valueType === CustomFieldValueType.Flag) {
        if (cfv?.value === "true") {
            return CustomFieldFlagYesGroup;
        } else if (cfv?.value === "false") {
            return CustomFieldFlagNoGroup;
        }
        return TasksWithoutCustomFieldValueGroup;
    } else if (valueType === CustomFieldValueType.Date) {
        let datePlain = null;
        if (cfv?.value) {
            try {
                datePlain = Temporal.PlainDate.from(cfv.value);
            } catch  {
                datePlain = null;
            }
        }
        return dueDateCategoryToString(getDueDateCategory(datePlain, todayPlainDate));
    }
    return TasksWithoutCustomFieldValueGroup;
}
/**
 * Gets the due date category for a plain date
 * @param duePlainDate The due date as PlainDate, or undefined/null for no date
 * @param todayPlainDate Today's PlainDate for comparison. Defaults to current date in system timezone.
 * @returns Due date category
 */ export function getDueDateCategory(duePlainDate, todayPlainDate = getTodayPlainDate()) {
    if (!duePlainDate) {
        return DueDateCategory.NoDate;
    }
    if (isToday(duePlainDate, todayPlainDate)) {
        return DueDateCategory.Today;
    } else if (isPast(duePlainDate, todayPlainDate)) {
        // TODO: PBI #2824793 - Split DueDateCategory and use Past here instead of Late
        return DueDateCategory.Late;
    } else if (isTomorrow(duePlainDate, todayPlainDate)) {
        return DueDateCategory.Tomorrow;
    } else if (isInCurrentWeek(duePlainDate, todayPlainDate)) {
        return DueDateCategory.ThisWeek;
    } else if (isInNextWeek(duePlainDate, todayPlainDate)) {
        return DueDateCategory.NextWeek;
    } else {
        return DueDateCategory.Future;
    }
}
/** Single source of truth for DueDateCategory ↔ string mappings. */ const DUE_DATE_CATEGORY_STRINGS = {
    [DueDateCategory.Late]: "Overdue",
    [DueDateCategory.Today]: "Today",
    [DueDateCategory.Tomorrow]: "Tomorrow",
    [DueDateCategory.ThisWeek]: "This week",
    [DueDateCategory.NextWeek]: "Next week",
    [DueDateCategory.Future]: "Later",
    [DueDateCategory.NoDate]: "No date",
    [DueDateCategory.SomeDate]: "Some date"
};
/** Inverse lookup built from the shared record. */ const STRING_TO_DUE_DATE_CATEGORY = Object.fromEntries(Object.entries(DUE_DATE_CATEGORY_STRINGS).map(([k, v])=>[
        v,
        Number(k)
    ]));
/**
 * Converts a group key string back to a DueDateCategory enum.
 * Reverse of dueDateCategoryToString.
 * @param key String group key (e.g. "Today", "Overdue")
 * @returns The matching DueDateCategory, or null if no match
 */ export function stringToDueDateCategory(key) {
    return STRING_TO_DUE_DATE_CATEGORY[key] ?? null;
}
/**
 * Converts DueDateCategory enum to string key
 * @param category Due date category enum value
 * @returns String representation for grouping
 */ export function dueDateCategoryToString(category) {
    return DUE_DATE_CATEGORY_STRINGS[category] ?? "Other";
}
/**
 * Find the placeholder index for a task being dragged to a column/group
 * @param data Tasks in destination group
 * @param columnValue Pivot value of destination group
 * @param item Item being dragged to group
 * @param timezone Timezone used for date calculations
 */ export function findPriorityPlaceholderIndex(data, columnValue, item, _timezone) {
    const index = data.findIndex((task)=>task.id === item.id);
    if (index !== -1) {
        return index;
    }
    // Apply column value
    const priority = ConvertPriorityNameToPriority(columnValue);
    const updatedTask = item.setProperty("priority", priority);
    // Add to group data
    const newData = [
        ...data,
        updatedTask
    ];
    // sort data
    newData.sort(compareByDueDate);
    // return new index
    return newData.indexOf(updatedTask);
}
/**
 * Find the placeholder index for a task being dragged to a column/group
 * @param data Tasks in destination group
 * @param columnValue Pivot value of destination group
 * @param item Item being dragged to group
 * @param timezone Timezone used for date calculations
 */ export function findDueDatePlaceholderIndex(data, columnValue, item, timezone) {
    const index = data.findIndex((task)=>task.id === item.id);
    if (index !== -1) {
        return index;
    }
    // Apply column value - ensure the placeholder includes a time component so comparisons that
    // expect an instant/zoned date do not throw (Temporal.Instant requires a time zone).
    const newDueDate = DateWithOptionalTime.fromDateCategory(columnValue, timezone);
    const updatedTask = item.setProperty("dueDateTime", newDueDate);
    // Add to group data
    const newData = [
        ...data,
        updatedTask
    ];
    // sort data
    newData.sort(compareByDueDate);
    // return new index
    return newData.indexOf(updatedTask);
}
/**
 * Find the placeholder index for a task being dragged to a finish-date column/group
 * @param data Tasks in destination group
 * @param columnValue Pivot value of destination group
 * @param item Item being dragged to group
 * @param timezone Timezone used for date calculations
 */ export function findFinishDatePlaceholderIndex(data, columnValue, item, timezone) {
    const index = data.findIndex((task)=>task.id === item.id);
    if (index !== -1) {
        return index;
    }
    // Apply column value
    const newFinishDate = DateWithOptionalTime.fromDateCategory(columnValue, timezone);
    const updatedTask = item.setProperty("finishDateTime", newFinishDate);
    // Add to group data
    const newData = [
        ...data,
        updatedTask
    ];
    // sort data
    newData.sort(compareByFinishDate);
    // return new index
    return newData.indexOf(updatedTask);
}
/**
 * Map of workflow states to display names (only some properties)
 */ export const ProjectManagerWorkflowStateToNameMap = {
    [CopilotWorkflowUnifiedState.Unassigned]: Strings.PlannerBoardPage_ProjectManagerWorkflowState_NotAssigned_ColumnName,
    [CopilotWorkflowUnifiedState.InProgress]: Strings.PlannerBoardPage_ProjectManagerWorkflowState_Assigned_ColumnName,
    [CopilotWorkflowUnifiedState.NeedsInfo]: Strings.PlannerBoardPage_ProjectManagerWorkflowState_NeedsInput_ColumnName,
    [CopilotWorkflowUnifiedState.Done]: Strings.PlannerBoardPage_ProjectManagerWorkflowState_Ready_ColumnName
};
/**
 * Returns the source group key for a task based on its container type.
 * - Group/Roster/TeamsChannel containers → SharedSourceGroup (shared with others)
 * - User containers → PersonalSourceGroup (personal plan, not shared)
 * - ToDo containers or TaskKind.ToDo → PrivateSourceGroup (flagged emails, To-Do)
 */ function getSourceGroup(task, planContainerTypeLookup) {
    if (task.kind === TaskKind.ToDo) {
        return PrivateSourceGroup;
    }
    const containerType = planContainerTypeLookup?.(task.planId);
    switch(containerType){
        case ContainerType.Group:
        case ContainerType.Roster:
        case ContainerType.TeamsChannel:
        case undefined:
            // Undefined means plan metadata hasn't loaded yet; default to Shared
            // because Group-backed plans are the most common container type.
            return SharedSourceGroup;
        case ContainerType.User:
            return PersonalSourceGroup;
        case ContainerType.ToDo:
            return PrivateSourceGroup;
        default:
            return SharedSourceGroup;
    }
}
